import React, { memo, ReactElement, useEffect, useRef, useState, Suspense, lazy } from 'react'
import { Box, Button, Grid, Tooltip, Typography, Accordion, AccordionDetails, AccordionSummary, Stack, IconButton, TextField, CircularProgress, LinearProgress } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import EditIcon from '@mui/icons-material/Edit'
import InfoIcon from '@mui/icons-material/Info'
import RefreshIcon from '@mui/icons-material/Refresh'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import stylesheet from './detail-container.stylesheet'
import ProgramTargetsComponent from './measurments/program-targets.component'
import OtherMedicationsComponent from './medications/other-medications.component'
import ProgramDataIntakeComponent from './measurments/program-data-intake.component'
import ProgramSpecificConditionsComponent from './conditions/program-specific-conditions.component'
import ProgramMedicationsComponent from './medications/program-medications.component'
import ProgramMonitorsComponent from './medications/program-monitors.component'
import SymptomEvaluationsComponent from './medications/symptom-evaluations.component'
import ProgramLifestylesComponent from './medications/program-lifestyles.component'
import ProgramExperimentsComponent from './medications/program-experiments.component'
import OtherMeasurmentComponent from './measurments/other-measurment.component'
import CalculatorsComponent from './measurments/calculators.component'
import PatientMedIntolrences from './medications/med-intolerance.component'
import OtherConditionsComponent from './conditions/other-conditions.component'
import ICD10CodesComponent from './conditions/ICD10-codes.component'
import ClinicalScoresComponent from './measurments/calculated-values.component'
import ProgramProblemsComponent from './medications/program-problems.component'
import { currentStatus } from '../../../redux/add-control-slice'
import { RootState, useAppThunkDispatch } from '../../../redux/store'
import { setEigRanking } from '../../../redux/common-data-slice'
import ColorBubbleComponent from './common/color-bubble.component'
import ErrorBoundary from '../../homepage/error-boundary.component'
import { PatientService } from '../../../services/patient.service'
import Helper from '../../../utils/helper'
import AddProgramDialogComponent from './info-header/add-program.component'
import MedReconcilationComponent from './medications/imported-med.component'
import moment from 'moment'
import MedReconciliationDialogComponent from './info-header/med-reconciliation-dialog.component'
import ProgramToxicitiesComponent from './medications/program-toxicities.component';
import { useUpdateDataAction } from '../../../utils/update-details-hook';
import DiagnosisPlan from './diagnosis/diagnosis-plan.component';
import DiagnosticThresholdComponent from './measurments/diagnostic-threshold.component';
import DiagnosisTestsComponent from './diagnosis/diagnosis-tests.component';
import PatientClinicalInfoComponent from './medications/patient-clinical-info.component';
import PatientPharmacyComponent from './medications/patient-pharmacy.component';
import UpdatePatientInfoComponent from './diagnosis/update-patientInfo.component';
import AddTestDialogComponent from './diagnosis/add-test-dialog.component'
import RPMBillingComponent from './rpm/rpm-billing.component'
import AIReviewComponent from './ai-review/ai-review.component'

// Lazy load the search component to speed up initial page load and dialog opening
const SearchQuesionnariesComponent = lazy(() => import('../../questionnaires/search-quesionnaries.component'));

interface IProps {
  patientInfo: any
  parent: string,
  group?: any,
  subGroup?: any,
  headerName: string | ReactElement
  cProgram?: string
  classes: ClassNameMap
}

const DetailCardComponent: React.FC<IProps> = ({
  patientInfo,
  parent,
  group,
  subGroup,
  headerName,
  cProgram,
  classes,
}) => {
  const [diagnosisData, setDiagnosisData] = useState([] as any)
  const [recommendedDiagnosis, setRecommendedDiagnosis] = useState([] as any)
  const [groupData, setGroupData] = useState({} as any)
  const [isAddProblem, setIsAddProblem] = useState(false)
  const [isPatientInfo, setIsPatientInfo] = useState(null) as any
  const [problemList, setProblemList] = useState([] as any)
  const [openPrgMed, setOpenPrgMed] = useState(false)
  const [openQuestionnarie, setOpenQuestionnarie] = useState(false)
  const [isAddTest, setIsAddTest] = useState(null) as any
  const [syncLoading, setSyncLoading] = useState(false)
  const [icd10SyncLoading, setIcd10SyncLoading] = useState(false)
  const [intoleranceSyncLoading, setIntoleranceSyncLoading] = useState(false)
  const [prioritizing, setPrioritizing] = useState(false)
  const [deferringAll, setDeferringAll] = useState(false)
  const [careTeamManagerOpen, setCareTeamManagerOpen] = useState(false)
  const [pharmacyManagerOpen, setPharmacyManagerOpen] = useState(false)

  const dispatch = useAppThunkDispatch()
  const patientService = new PatientService()
  const inputEl = useRef(null) as any;
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const program_enrollments = useSelector((state: RootState) => state.patientDetail.patientDetail?.program_enrollments || [])
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const { updatePatientData } = useUpdateDataAction(patientDetail.id);

  useEffect(() => {
    const next = checkData()
    setGroupData((prev: any) => (prev[parent] === next ? prev : { ...prev, [parent]: next }))

    const calculators = patientDetail?.measurements?.calculators || []
    setDiagnosisData(calculators.filter((i: any) => i.category === 'diagnosis'))
    setRecommendedDiagnosis(patientDetail?.measurements?.recommended_calculators)
    // Initialize EIG ranking from server-computed ordering
    const serverEigRanking = patientDetail?.measurements?.eig_ranking
    if (serverEigRanking?.length > 0) {
      dispatch(setEigRanking(serverEigRanking))
    }
  }, [patientDetail]);

  const renderSwitch = (param: string) => {
    let component
    switch (param) {
      case 'ME-PDI':
        component = (
          <ProgramDataIntakeComponent
            input={{
              patientId: patientInfo?.id,
              program_checks: patientInfo?.measurements?.program_checks,
              isActionItems: patientInfo?.isActionItems,
              isOverrideItems: patientInfo?.isOverrideItems,
              actionProgram: patientInfo?.actionProgram
            }}
          />
        )
        break
      case 'ME-CS':
        component = <ClinicalScoresComponent
          input={{
            parent: 'CS',
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'ME-THR':
        component = <DiagnosticThresholdComponent
          input={{
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'ME-CAL':
        component = (
          <CalculatorsComponent
            input={{
              patientId: patientInfo?.id,
              calculators: patientInfo?.measurements?.calculators,
              isActionItems: patientInfo?.isActionItems,
              isOverrideItems: patientInfo?.isOverrideItems,
              actionProgram: patientInfo?.actionProgram
            }}
          />
        )
        break
      case 'ME-OTHR':
        component = (
          <OtherMeasurmentComponent
            input={{
              patientId: patientInfo?.id,
              others: patientInfo?.measurements?.others,
              isActionItems: patientInfo?.isActionItems,
              isOverrideItems: patientInfo?.isOverrideItems,
            }}
          />
        )
        break
      case 'DIA-PLAN':
        component = <DiagnosisPlan
          input={{
            ...patientInfo,
            // isActionItems: patientInfo?.isActionItems,
            // isOverrideItems: patientInfo?.isOverrideItems,
            // actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'AI-REVIEW':
        component = <AIReviewComponent
          patientId={patientInfo?.id}
          patientRefId={patientInfo?.ref_id}
          classes={classes}
          onDataChanged={() => updatePatientData({ isMeasurement: true, isConditions: true })}
        />
        break
      case 'MD-PRMD':
        component = <ProgramMedicationsComponent
          patientInfo={{
            ...patientInfo,
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'DIA-TEST':
        component = <DiagnosisTestsComponent
          input={{
            ...patientInfo,
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }}
        />
        break
      case 'MD-TOXEVAL':
        component = <ProgramToxicitiesComponent
          input={{
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'MD-PATIENT-INFO':
        component = <PatientClinicalInfoComponent
          input={{
            type: 'patient-info',
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'MD-CLI-TEAM':
        component = <PatientClinicalInfoComponent
          input={{
            type: 'clinical-info',
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }}
          editCareTeamOpen={careTeamManagerOpen}
          onCloseCareTeamEdit={() => setCareTeamManagerOpen(false)}
        />
        break
      case 'MD-CLI-VISIT':
        component = <PatientClinicalInfoComponent
          input={{
            type: 'clinic-visits',
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'MD-PHARMACY':
        component = <PatientPharmacyComponent
          editPharmacyOpen={pharmacyManagerOpen}
          onClosePharmacyEdit={() => setPharmacyManagerOpen(false)}
        />
        break
      case 'MD-LS':
        component = <ProgramLifestylesComponent input={{
          parent: 'MD',
          isActionItems: patientInfo?.isActionItems,
          isOverrideItems: patientInfo?.isOverrideItems,
          actionProgram: patientInfo?.actionProgram
        }} />
        break
      case 'MD-EXP':
        component = <ProgramExperimentsComponent input={{
          parent: 'MD',
          isActionItems: patientInfo?.isActionItems,
          isOverrideItems: patientInfo?.isOverrideItems,
          actionProgram: patientInfo?.actionProgram
        }} />
        break
      case 'MD-PRMT':
        component = <ProgramMonitorsComponent input={{
          parent: 'MD',
          isActionItems: patientInfo?.isActionItems,
          isOverrideItems: patientInfo?.isOverrideItems,
          actionProgram: patientInfo?.actionProgram
        }} />
        break
      case 'MD-SXEVAL':
        component = <SymptomEvaluationsComponent input={{
          parent: 'MD',
          isActionItems: patientInfo?.isActionItems,
          isOverrideItems: patientInfo?.isOverrideItems,
          actionProgram: patientInfo?.actionProgram
        }} />
        break
      case 'MD-IMMD':
        component = <OtherMedicationsComponent patientInfo={patientInfo} currentProgram='' />
        break;
      case 'MD-RECON':
        component = <MedReconcilationComponent
          patientInfo={{
            ...patientInfo,
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }}
        />
        break
      case 'CO-PRB':
        component = <ProgramProblemsComponent
          input={{
            parent: 'PRB',
            patientInfo: patientInfo,
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }}
        />
        break
      case 'CO-DIA':
        component = (
          <CalculatorsComponent
            input={{
              patientId: patientInfo?.id,
              calculators: patientInfo?.measurements?.calculators,
              type: 'diagnosis',
              isActionItems: patientInfo?.isActionItems,
              isOverrideItems: patientInfo?.isOverrideItems,
              actionProgram: patientInfo?.actionProgram
            }}
          />
        )
        break
      case 'CO-PSC':
        component = <ProgramSpecificConditionsComponent
          input={{
            parent: 'PSC',
            isActionItems: patientInfo?.isActionItems,
            isOverrideItems: patientInfo?.isOverrideItems,
            actionProgram: patientInfo?.actionProgram
          }} />
        break
      case 'MD-PRM':
        component = <PatientMedIntolrences patientInfo={patientInfo} classes={classes} />
        break
      // case 'MD-PRM':
      //     return <PatientMedIntolrences patientInfo={patientInfo} classes={classes} ></PatientMedIntolrences>;
      case 'CO-OC':
        component = <OtherConditionsComponent input={{ parent: 'OC' }} />
        break
      case 'CO-ICD10':
        component = <ICD10CodesComponent input={{ parent: 'ICD10' }} />
        break

      // RPM Billing
      case 'RPM-BILLING':
        component = <RPMBillingComponent />
        break

      default:
        component = <Typography component={'span'} pl={2} className={classes.desc}>Not Available</Typography>
        break
    }
    return component
  }

  const handleQuestionnarielose = () => setOpenQuestionnarie(false)

  const createHeader = (_headerName: any) => {
    let output
    switch (_headerName) {
      case 'Other conditions':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Other medications':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Medication restrictions':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Relevant comorbidities':
        output = subHeaderFormat(_headerName, 'Recalculate')
        break
      case 'ICD10 codes':
        output = subHeaderFormat(_headerName, 'Recalculate')
        break
      case 'Problems for evaluation':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Imported medications':
        output = importedMedHeader(_headerName, '')
        break
      case 'Symptom evaluations':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Interventions':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Clinical assessments':
        output = subHeaderFormat(_headerName, 'Search')
        break
      case 'Clinical team':
        output = subHeaderFormat(_headerName, '')
        break
      case 'Clinic visit':
        output = subHeaderFormat(_headerName, '')
        break
      case 'Referral':
        output = subHeaderFormat(_headerName, '')
        break
      case 'Tests':
        output = subHeaderFormat(_headerName, 'Add new')
        break
      case 'Pharmacy':
        output = subHeaderFormat(_headerName, '')
        break
      default:
        output = _headerName
        break
    }
    return output
  }

  const onAddOrManageClick = (event: any) => {
    if (inputEl.current && inputEl.current.getAttribute('aria-expanded') == 'true') {
      event.stopPropagation()
      event.preventDefault()
    }
    let temp = {}
    if (parent === 'CO-OC' || parent === 'MD-IMMD' || parent === 'MD-PRM' || parent == 'MD-PRMT' || parent == 'CO-ICD10' || parent == 'MD-LS' || parent === 'MD-SXEVAL' || parent === 'MD-EXP') {
      temp = { key: parent, value: true }
      dispatch(currentStatus(temp))
    }
  }

  const handleAddProblem = (event: any) => {
    event.stopPropagation()
    event.preventDefault()

    patientService.getProblemsList().then((response: any) => {
      let prbData = Object.keys(response?.data)?.map((i: any) => {
        const isPrbExists = patientDetail?.conditions?.problems?.filter((a: any) => a.name === i)
        return { name: i, display_name: response?.data[i], checked: isPrbExists?.length > 0 ? true : false }
      }) || []
      setProblemList([...prbData]?.sort((a: any, b: any) => a.display_name > b.display_name ? 1 : -1))
    }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center())
    )
    setIsAddProblem(true)
  }

  const handlePrgDialogClose = () => setIsAddProblem(false)

  const handleMedDialogClose = (data: any) => {
    setOpenPrgMed(false)
    if (data?.isReconcile)
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
  }

  const handlePrgDialogSubmit = (data: any) => {
    handlePrgDialogClose()
    data && patientService.createProblem(patientDetail.id, { 'problem_name': data })
      .then((_res: any) => {
        updatePatientData({ isConditions: true })
      }).catch((error: any) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const handlePatientInfo = (event: any, type: any) => {
    event.stopPropagation()
    event.preventDefault()
    // Open CareTeamManager for Clinical team regardless of AIDA mode
    if (type === 'Clinical team') {
      setCareTeamManagerOpen(true)
    } else if (type === 'Pharmacy') {
      setPharmacyManagerOpen(true)
    } else {
      setIsPatientInfo(type)
    }
  }

  const onRefreshPSCScore = (event: any, isICD = false) => {
    getCalculatedPSC(isICD)
    event.stopPropagation()
    event.preventDefault()
  }

  const getCalculatedPSC = async (isICD: boolean) => {
    try {
      if (isICD) {
        await patientService.recalculateICD10(patientDetail.id)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      } else {
        await patientService.getCalculatedPSC(patientDetail.id)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      }
    } catch (error) {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    }
  }

  const syncICD10ToElation = async (event: any) => {
    event.stopPropagation()
    event.preventDefault()
    setIcd10SyncLoading(true)
    try {
      const response = await patientService.syncICD10ToElation(patientDetail.id)
      const result = response.data
      if (result.success) {
        const synced = result.synced?.length || 0
        const skipped = result.skipped?.length || 0
        if (synced > 0) {
          toast.success(`Synced ${synced} ICD-10 codes to Elation`, Helper.bottom_center())
        } else if (skipped > 0) {
          toast.info(`All ${skipped} ICD-10 codes already exist in Elation`, Helper.bottom_center())
        } else {
          toast.info('No ICD-10 codes to sync', Helper.bottom_center())
        }
      } else {
        // Show detailed error if available
        const errorDetails = result.errors?.map((e: any) => `${e.code}: ${e.error}`).join(', ')
        toast.error(errorDetails || result.message || 'Failed to sync ICD-10 codes', Helper.bottom_center())
      }
    } catch (error) {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    } finally {
      setIcd10SyncLoading(false)
    }
  }

  const syncIntolerancesToElation = async (event: any) => {
    event.stopPropagation()
    event.preventDefault()
    setIntoleranceSyncLoading(true)
    try {
      const response = await patientService.syncIntolerancesToElation(patientDetail.id)
      const result = response.data
      if (result.success) {
        const synced = result.synced?.length || 0
        const skipped = result.skipped?.length || 0
        if (synced > 0) {
          toast.success(`Synced ${synced} medication restrictions to Elation`, Helper.bottom_center())
        } else if (skipped > 0) {
          toast.info(`All ${skipped} restrictions already exist in Elation`, Helper.bottom_center())
        } else {
          toast.info('No medication restrictions to sync', Helper.bottom_center())
        }
      } else {
        // Show detailed error if available
        const errorDetails = result.errors?.map((e: any) => `${e.name}: ${e.error}`).join(', ')
        toast.error(errorDetails || result.message || 'Failed to sync medication restrictions', Helper.bottom_center())
      }
    } catch (error) {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    } finally {
      setIntoleranceSyncLoading(false)
    }
  }

  const searchClicalAssessment = () => {
    return <Box sx={{ height: 'webkit-fill-available', }}>
      <Button variant="text" title='Edit' className={classes.expressModeBtn}
        sx={{
          padding: '0rem !important',
          minWidth: '1rem',
          '&.Mui-disabled': { border: 'none !important', }
        }}
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation()
          console.time('[PERF] Magnifying glass click to dialog render')
          console.log('[PERF] Magnifying glass clicked - diagnosisData:', diagnosisData?.length, 'recommendedDiagnosis:', recommendedDiagnosis?.length)
          setOpenQuestionnarie(true)
        }}
      >
        <SearchIcon className={classes.subHeadername} sx={{ fill: openQuestionnarie ? 'var(--color-primary)' : 'inhert' }} />
      </Button>
    </Box>
  }


  const handlePrioritizeTests = () => {
    setPrioritizing(true)
    patientService.prioritizeTests(patientDetail?.ref_id).then(() => {
      setPrioritizing(false)
      updatePatientData({ isMeasurement: true, isConditions: true })
    }).catch((err: any) => {
      setPrioritizing(false)
      toast.error(Helper.getError(err), Helper.bottom_center())
    })
  }

  const recommendedTests = (patientDetail?.diagnosis?.diagnostic_tests || [])
    .filter((t: any) => t?.current_status === 'recommended')

  const handleDeferAllRecommended = () => {
    if (recommendedTests.length === 0) return
    setDeferringAll(true)
    Promise.all(
      recommendedTests.map((t: any) =>
        patientService.putDiagnosticTest(t.id, { status: 'deferred' })
      )
    ).then(() => {
      setDeferringAll(false)
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
    }).catch((err: any) => {
      setDeferringAll(false)
      toast.error(Helper.getError(err), Helper.bottom_center())
    })
  }

  const subHeaderFormat = (_headerName: string, _rightLabel: string) => {
    return (
      <Stack
        direction={'row'}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '0.7rem !important',
          width: '100%',
          paddingRight: ['Other medications', 'Medication restrictions'].includes(_headerName) ? '7px' : '0px',
        }}
      >
        <Typography className={classes.subHeadername} variant="h6" sx={{ lineHeight: '0.8rem !important' }}>
          {_headerName}
        </Typography>
        {!patientInfo?.actionProgram && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {['Clinical assessments'].includes(_headerName) ? (
              searchClicalAssessment()
            ) : ['Referral', 'Clinical team', 'Clinic visit', 'Pharmacy'].includes(_headerName) ? (
              <Button
                variant="text"
                title="Edit"
                className={classes.expressModeBtn}
                sx={{
                  padding: '0rem !important',
                  marginRight: '-0.5rem !important',
                  minWidth: '1rem',
                  '&.Mui-disabled': { border: 'none !important' },
                }}
                disabled={patientDetailStatus === 'loading'}
                onClick={(e) => handlePatientInfo(e, _headerName)}
              >
                <EditIcon className={classes.editDefIcon} />
              </Button>
            ) : (
              <Stack direction={'row'} spacing={0.2} alignItems={'center'} justifyContent={'flex-end'}>
                {/* Elation sync button for ICD10 codes - left of Recalculate */}
                {_headerName === 'ICD10 codes' && !patientDetail.is_test_patient && Helper.checkSouceDeatils(patientDetail.source_details) && (
                  <Tooltip title="Sync to Elation">
                    <span
                      style={{ cursor: icd10SyncLoading ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', marginRight: '0.1rem' }}
                      onClick={icd10SyncLoading ? undefined : syncICD10ToElation}
                    >
                      {icd10SyncLoading ? <CircularProgress size={8} /> : <RefreshIcon style={{ fontSize: '10px', color: '#757575' }} />}
                    </span>
                  </Tooltip>
                )}
                {/* Elation sync button for Medication restrictions - left of Add new */}
                {_headerName === 'Medication restrictions' && !patientDetail.is_test_patient && Helper.checkSouceDeatils(patientDetail?.source_details) && (
                  <Tooltip title="Sync to Elation">
                    <span
                      style={{ cursor: intoleranceSyncLoading ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', marginRight: '0.1rem' }}
                      onClick={intoleranceSyncLoading ? undefined : syncIntolerancesToElation}
                    >
                      {intoleranceSyncLoading ? <CircularProgress size={8} /> : <RefreshIcon style={{ fontSize: '10px', color: '#757575' }} />}
                    </span>
                  </Tooltip>
                )}
                {_headerName === 'Tests' && (
                  <Tooltip title="Run EIG-based test prioritization">
                    <span>
                      <Button
                        size="small"
                        className={classes.expressModeBtn}
                        sx={{ padding: '0rem !important', minWidth: '2rem !important' }}
                        onClick={handlePrioritizeTests}
                        disabled={prioritizing}
                      >
                        {prioritizing ? <CircularProgress size={8} /> : <AutoFixHighIcon className={classes.addCircleButton} />}
                        <Typography variant="subtitle2" className={classes.desc}>Prioritize</Typography>
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {_headerName === 'Tests' && recommendedTests.length > 0 && (
                  <Tooltip title={`Defer all ${recommendedTests.length} recommended test${recommendedTests.length === 1 ? '' : 's'}`}>
                    <span>
                      <Button
                        size="small"
                        className={classes.expressModeBtn}
                        sx={{ padding: '0rem !important', minWidth: '2rem !important' }}
                        onClick={handleDeferAllRecommended}
                        disabled={deferringAll}
                      >
                        {deferringAll ? <CircularProgress size={8} /> : <HourglassBottomIcon className={classes.addCircleButton} />}
                        <Typography variant="subtitle2" className={classes.desc}>Defer all</Typography>
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  className={isAddProblem && _headerName === 'Problems for evaluation' ? classes.activeBtn : classes.expressModeBtn}
                  sx={{ padding: '0rem !important', minWidth: '2rem !important' }}
                  onClick={(e) =>
                    ['Tests'].includes(_headerName)
                      ? setIsAddTest('recommended')
                      : _headerName === 'Relevant comorbidities' || _headerName === 'ICD10 codes'
                      ? onRefreshPSCScore(e, _headerName === 'ICD10 codes' ? true : false)
                      : _headerName === 'Problems for evaluation'
                      ? handleAddProblem(e)
                      : onAddOrManageClick(e)
                  }
                >
                  {!['Clinical assessments', 'Relevant comorbidities', 'ICD10 codes'].includes(_headerName) && <AddCircleOutlinedIcon className={classes.addCircleButton} />}
                  <Typography variant="subtitle2" className={classes.desc}>
                    {_rightLabel}
                  </Typography>
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    )
  }

  const handleReconcile = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenPrgMed(true)
  }

  const syncElationMeds = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    const srcDetails = patientDetail?.source_details[0] || []
    if (srcDetails?.length > 0) {
      setSyncLoading(true)
      const type = srcDetails[0]
      const mrn = srcDetails[1]
      patientService.syncELATIONPatientMedications(mrn)
        .then((_res: any) => {
          console.log("Synced the data")
          // setSyncOpen(false)
          updatePatientData({ isTreatments: true })
        })
        .catch((error: any) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
        .finally(() => setSyncLoading(false))
    }
    else {
      toast.error("Elation external ID is missing for patient", Helper.bottom_center())
    }
  }

  const getEnrollmentId = () => patientDetail?.program_enrollments
    ?.filter((m: any) => m.program?.includes('_evaluation'))
    ?.[0]?.enrollment_id || null

  const importedMedHeader = (_headerName: string, _rightLabel: string) => {
    return (
      <Grid container justifyContent="space-between" direction='row' alignItems="center">
        <Grid item style={{ width: patientDetail?.medications?.last_sync_timestamp ? '85%' : '60%' }}>
          <Typography className={classes.subHeadername} component='span' variant="h6">
            {`${_headerName}`}
          </Typography>
          <Typography component='span' variant='body2' color="textSecondary" className={classes.titleSmall} pl={0.3}>
            {patientDetail?.medications?.last_sync_timestamp &&
              <>
                Synced on {moment.unix(patientDetail?.medications?.last_sync_timestamp)?.format("MMM DD, YYYY h:mm a")}
              </>
            }
          </Typography>
        </Grid>
        <Grid item style={{ width: 'auto' }}>
          <Stack direction={'row'} spacing={0.5} justifyContent={'flex-end'}>
            {Object.keys(patientDetail?.medications?.non_synced_medications)?.length > 0 &&
              <Stack direction={'row'} spacing={0.2} justifyContent={'flex-end'}>
                <Tooltip
                  title={patientDetail?.medications?.is_med_reconciled ? `Reconciled` : `Reconcile pending`}
                >
                  <InfoIcon className={classes.reconcileListIcon}
                    sx={{ fill: patientDetail?.medications?.is_med_reconciled ? 'inherit' : 'var(--color-primary) !important' }}
                  />
                </Tooltip>
                {/* <Typography component='span' color="textSecondary" className={classes.titleSmall}>
                  {patientDetail?.medications?.is_med_reconciled ? `Reconciled` : `Reconcile pending`}
                </Typography> */}
                <Tooltip title={`Med reconcile list`}>
                  <AddCircleOutlinedIcon onClick={(e) => handleReconcile(e)} className={classes.reconcileListIcon} />
                </Tooltip>
              </Stack>
            }
            {/* <Tooltip title={`Sync elation medications`}>
              {syncLoading
                ? <CircularProgress sx={{ marginTop: '0.2rem' }} size={10} />
                : <SyncIcon fontSize="small" className={classes.reconcileListIcon} onClick={(e) => syncElationMeds(e)} />
              }
            </Tooltip> */}
          </Stack>
        </Grid>
      </Grid>
    )
  }

  const renderProgTargets = () => {
    const targetObjList = (patientInfo?.isActionItems === true && patientInfo?.actionProgram)
      ? [patientInfo?.actionProgram]
      : Array.from(new Set(
        patientDetail?.measurements?.program_targets?.map((e: { program: string }) => e.program))
      )
    return targetObjList?.map((p: any, i: number) => (
      <React.Fragment key={`props-${i}`}>
        {selectedProgram.includes(p) && (
          <Box className={classes.programTargetBox}>
            <ProgramTargetsComponent
              input={{
                patientId: patientInfo?.id,
                title: <>
                  {`${program_enrollments.find((e: any) => e.program === p)?.program_display_name}`}{' '}
                  <ColorBubbleComponent selectedProgramList={[p]} />
                </>,
                program_targets: patientInfo?.measurements?.program_targets,
                cProgram: p,
                isActionItems: patientInfo?.isActionItems,
                isOverrideItems: patientInfo?.isOverrideItems,
                actionProgram: patientInfo?.actionProgram
              }}
            />
          </Box>
        )}
      </React.Fragment>
    ))
  }

  const renderDiaThreshold = () => {
    return <DiagnosticThresholdComponent
      input={{
        isActionItems: patientInfo?.isActionItems,
        isOverrideItems: patientInfo?.isOverrideItems,
        actionProgram: patientInfo?.actionProgram
      }}
    />
  }

  const MEPTRSetup = (title: any) => {
    return <Accordion square defaultExpanded className={classes.patientAccrodionBg}>
      <AccordionSummary
        ref={inputEl}
        expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />}
      >
        <Typography className={classes.subHeader} component={'span'} variant="h6">
          {createHeader(title)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {title === 'Diagnostic thresholds' ? renderDiaThreshold() : renderProgTargets()}
      </AccordionDetails>
    </Accordion >
  }


  const checkData = () => {
    if (!patientDetail) return true;

    if (parent === 'ME-OTHR') return false;

    // RPM Billing should be collapsed by default
    if (parent === 'RPM-BILLING') return false;

    // Imported medications should be collapsed by default
    if (parent === 'MD-RECON') return false;

    // Clinical Team should always be expanded by default
    if (parent === 'MD-CLI-TEAM') {
      return true
    }

    if (['MD-PATIENT-INFO', 'MD-CLI-VISIT'].includes(parent)) {
      return Helper.isAmyloidPrg(patientDetail?.program_enrollments)
    }

    // Pharmacy accordion should be expanded by default
    if (parent === 'MD-PHARMACY') {
      return true
    }

    // MD-EXP (Experiments) belongs here rather than falling through to the
    // group/subGroup lookup below: ProgramExperimentsComponent fetches its own
    // experiments from the API, so `patientDetail.medications.experiments` is never
    // populated and the generic rule would always collapse the section.
    const alwaysTrueParents = ['MD-PRMD', 'CO-DIA', 'CO-PSC', 'MD-SXEVAL', 'MD-EXP'];
    if (alwaysTrueParents.includes(parent)) return true;

    // AI Review should be collapsed by default
    if (parent === 'AI-REVIEW') return false;

    if (!group || !subGroup) return true;

    try {
      const groupData = patientDetail[group];
      if (!groupData) return true;

      const dataLen = Object.keys(groupData[subGroup] || {}).length;
      switch (parent) {
        case 'MD-LS':
          return dataLen > 0 ||
            Object.keys(groupData['stopped_lifestyles'] || {}).length > 0;
        case 'MD-PRMT':
          return dataLen > 0 ||
            Object.keys(groupData['stopped_monitors'] || {}).length > 0;
        default:
          return dataLen > 0;
      }
    } catch (error) {
      console.error('Error in checkData:', error);
      return true
    }
  };

  const renderGroups = () => {
    return <Box pt={0.5}>
      {(['ME-PTR', 'ME-THR'].includes(parent)) &&
        MEPTRSetup('ME-THR' === parent ? 'Diagnostic thresholds' : 'Program targets')
      }
      {!['ME-PTR', 'ME-THR'].includes(parent) && (<>
        {parent === 'DIA-STATUS'
          ? <Box sx={{ padding: '0px 10px 0px 8px !important' }}>
            {renderSwitch(parent)}
          </Box>
          : <Accordion
            square
            expanded={Boolean(groupData[parent] || false)}
            onChange={(e, expanded) => setGroupData({ ...groupData, [parent]: expanded })}
            className={classes.patientAccrodionBg}
          >
            <AccordionSummary
              ref={inputEl}
              className={classes.zeroTopPadding}
              expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />}
            >
              <Typography className={classes.subHeader} variant="h6">
                {createHeader(headerName)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.zeroPadding}>
              {renderSwitch(parent)}
            </AccordionDetails>
          </Accordion>
        }
      </>)}
    </Box>
  }

  return (
    <>
      <ErrorBoundary>
        {renderGroups()}

        {isAddProblem &&
          <AddProgramDialogComponent
            open={isAddProblem}
            item={{
              title: 'Add Problem',
              label: 'Select Problem',
              prgData: problemList
            }}
            handleDialogClose={handlePrgDialogClose}
            handleSubmit={handlePrgDialogSubmit}
          />
        }
        {openPrgMed && (
          <MedReconciliationDialogComponent
            open={openPrgMed}
            item={{
              title: 'Med reconcile list',
              isAllList: true
            }}
            handleDialogClose={handleMedDialogClose}
          />
        )}
        {openQuestionnarie && (
          <Suspense fallback={<LinearProgress />}>
            <SearchQuesionnariesComponent
              open={openQuestionnarie}
              title={'Clinical assessments'}
              diaItem={diagnosisData}
              diaRecommended={recommendedDiagnosis}
              handleClose={handleQuestionnarielose}
            />
          </Suspense>
        )}
        {isPatientInfo !== null &&
          <UpdatePatientInfoComponent
            open={Boolean(isPatientInfo)}
            input={{
              title: `Update ${(isPatientInfo).toLowerCase()}`,
              type: isPatientInfo,
              patientId: patientDetail.id,
            }}
            handleDialogClose={() => setIsPatientInfo(null)}
          />
        }
        {isAddTest &&
          <AddTestDialogComponent
            open={Boolean(isAddTest)}
            input={{
              title: `Add test`,
              isAdd: true,
              type: isAddTest,
              enrollment_id: getEnrollmentId(),
            }}
            handleDialogClose={() => setIsAddTest(null)}
          />
        }
      </ErrorBoundary>
    </>
  )
}

export default memo(withStyles(stylesheet)(DetailCardComponent))